// Manage channels and control change events for synths
// this can be shared by sampler, sf2, etc.

{ remove } from ../util.civet
ReverbWorklet, { REVERB_RT60 } from ./reverb-worklet.civet
{ createChorusNode, type ChorusNode } from ./chorus.civet
{ createDelayNode, DELAY_MACROS, DELAY_DEFAULTS, type GsDelayNode } from ./delay.civet

type { MidiChannel } from ../midi/channel-data.civet
type { Voice } from ./index.civet

{ max, pow } := Math

// Wet-return trim, MEASURED against real SC-55mk2 firmware (Nuked-SC55): with the FDN's
// DC-normalized combs at the measured 2s RT60, our tail ran ~8.5dB hotter than hardware
// at the same CC 91 send (-24.7 vs -33.5 dB below peak, 0.5s after a staccato hit).
// 10^(-8.5/20). Applied under the GS reverb-level knob, so CC/SysEx ratios still hold.
REVERB_RETURN_TRIM := 0.376

DummyParam := ->
  setValueAtTime: ->
  linearRampToValueAtTime: ->

DummyConstantSourceNode := ->
  connect: ->
  stop: ->
  offset: DummyParam()

// NOTE:
// Param value changes should be smooth based on ticks/beat and
// tempo such that a change every tick will result in a straight line of
// change through time without any discontinuities or flat shelves
// i.e. if 96 ticks/beat at 120 bpm (2 beat/s) = 192 ticks / s
// setValueAtTime currentValue, t
// linear/exponentialRampToValueAtTime targetValue, t + 1/192

// Hold-then-ramp an AudioParam: cancel queued automation (where supported --
// FF lacks cancelAndHoldAtTime, https://bugzilla.mozilla.org/show_bug.cgi?id=1308431),
// anchor the current value at t, and ramp to the target over one tick.
//
// The anchor is unconditional: linearRampToValueAtTime ramps from the
// *previous timeline event*, and cancelAndHoldAtTime inserts none when the
// timeline is empty (Chromium and node-web-audio-api both), so without it
// the first bend of a song glides from note start instead of from the bend.
// The cost is a small step when a new target lands mid-ramp; ramps last one
// tick, so that needs two events on the same tick.
holdAndRamp := (param: AudioParam, previous: number, target: number, t: number, epsilon: number) ->
  if param.cancelAndHoldAtTime
    param.cancelAndHoldAtTime t
  param.setValueAtTime previous, t
  param.linearRampToValueAtTime target, t + epsilon

class Channel
  //@ts-expect-error
  channels: MidiChannel[]

  //@ts-expect-error
  channelOut: ChannelOut[]
  prevOut: GainNode?

  context: BaseAudioContext?

  // AudioWorkletNode rather than AudioNode: the GS reverb-time knob is one of its
  // AudioParams, so the type has to carry `parameters`.
  reverbBus: AudioWorkletNode?
  // Post-FDN return gain — the GS "reverb level" knob. Exists from bind (synchronously),
  // so a level set before the async worklet resolves isn't lost.
  reverbReturn: GainNode?
  chorusBus: ChorusNode?
  delayBus: GsDelayNode?

  // GS effect-block state, persisted across binds so a seek or re-render keeps the room
  // the file configured. `chorus`/`delay` hold partial setParams args (delay's are the
  // raw GS wire values).
  fx: { reverbLevel: number, rt60: number, chorus: Record<string, number>, delay: Record<string, number> } =
    { reverbLevel: 1, rt60: REVERB_RT60, chorus: {}, delay: {} }

  setVolume = (channelAudio: ChannelOut, channel: MidiChannel, v: number, t: number) =>
    { gain } .= channel

    gain = max gain, 0.00001

    epsilon := channel.shared.deltaTimeToSeconds 1
    param := channelAudio.gain

    // Same anchor rationale as holdAndRamp (exponential ramp variant): without
    // the setValueAtTime the first CC 7/11 of a song ramps from note start.
    if param.cancelAndHoldAtTime
      param.cancelAndHoldAtTime t
    param.setValueAtTime(gain, t)
    param.exponentialRampToValueAtTime(max(v * v, 0.00001), t + epsilon)

  // voice, audio node
  // c midi channel number
  // n midi note number
  addVoice(voice: Voice, c: number, n: number)
    if voice
      {input, reverb, tuning, voices} := @channelOut[c]
      voice.connect(input)

      // Per voice send, tapped ahead of the channel's pan and volume: the send amount is a
      // property of the zone, so it cannot be taken after a node the whole channel shares.
      // The consequence is that CC 7 does not scale a tail already in flight, the same way
      // it doesn't shorten a release envelope already running.
      //
      // The channel's CC 91 (its GM default is set in resetControllers) rides alongside
      // the zone generator, whichever asks for more: GS-era files drive their wet almost
      // entirely through CC 91 (soundfont zones typically send ≤8%), so honouring only
      // the zone left them effectively dry. Sampled at noteOn — these files write CC 91
      // once per channel during setup, not live.
      // Live CC 91/93 retunes these; base is the zone's own send, the floor a live CC
      // can't cut below.
      addSend := (bus: AudioNode?, base: number, level: number, cc: number) =>
        sendAmount := max base, level
        return unless bus and sendAmount and @context
        send := @context.createGain()
        send.gain.value = sendAmount
        voice.connect send
        send.connect bus
        voice.fxSends.push { cc, param: send.gain, base }

        voice.sources[0]?.bufferSource.addEventListener 'ended', ->
          try
            send.disconnect()
          catch e
            // already disconnected

      channel := @channels[c]
      addSend reverb, voice.reverbSend, channel.fx1, 0x5B
      addSend @channelOut[c].chorus, voice.chorusSend ?? 0, channel.fx3, 0x5D
      // Delay (CC 94 / drum NRPN 0x1F): SF2 has no delay-send generator, so the base is
      // purely what the drum setup asked for.
      addSend @channelOut[c].delay, voice.delaySend ?? 0, channel.fx4, 0x5E

      // connect tuning
      voice.sources.forEach ({bufferSource}) ->
        tuning.connect bufferSource.detune
        // disconnect tuning (FF will leak audio node references if we don't)
        // and it's also a good practice to clean up.
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1572524
        // https://bugzilla.mozilla.org/show_bug.cgi?id=897796
        bufferSource.addEventListener 'ended', ->
          try
            // TODO
            // not sure why sometimes this throws an error and says it has
            // already been disconnected
            // perhaps adding and removing nodes triggers a GC on disconnected
            // nodes before this listener fires?
            tuning.disconnect bufferSource.detune
          catch e
            // console.warn e

      voices.push { voice, n }

  // c midi channel number
  // n midi note number
  removeVoice(c: number, n: number)
    { tuning, voices } := @channelOut[c]
    [voice] := voices.filter ({n:_n}) -> _n is n

    if voice
      remove voices, voice

      // console.log "removeVoice", c, n, voice.voice.t
      return voice.voice

    // console.log "removeVoice", c, n, voice
    return

  // Move note n's voice out of the active list without releasing it — the sustain
  // pedal is down, so the key-up is deferred until the pedal lifts.
  sustainVoice(c: number, n: number)
    { voices, sustained } := @channelOut[c]
    [entry] := voices.filter ({n: _n}) -> _n is n
    if entry
      remove voices, entry
      sustained.push entry.voice

  // Everything the pedal was holding, cleared — the caller releases each one.
  takeSustained(c: number)
    { sustained } := @channelOut[c]
    out := sustained.slice()
    sustained.length = 0
    out

  // With a channel: CC 120, silence just that channel. Without: silence
  // everything (player stop). Pedal-held voices go silent too — CC 120 is
  // "all sound", not a key release.
  allSoundsOff(channel?: MidiChannel)
    silence := ({ voices, sustained }: ChannelOut) =>
      voices.forEach ({ voice }) =>
        voice?.mute()
      sustained.forEach (voice: Voice) =>
        voice?.mute()
      voices.length = 0
      sustained.length = 0

    if channel
      out := @channelOut?[channel.id]
      silence out if out
    else
      @channelOut?.forEach silence

  // Bind the synth to a given audio context with the given channel data
  // return an audio node that can be connected into that context
  bind(_channels: MidiChannel[], _context: BaseAudioContext)
    @channels = _channels
    context := @context = _context

    if @prevOut?
      @prevOut.disconnect()

    // Master volume, destination for all channels
    out := context.createGain()
    @prevOut = out

    @channelOut?.forEach (channelAudio) =>
      channelAudio.dispose()

    @reverbBus?.disconnect()
    @reverbBus = undefined
    reverbSends: GainNode[] := []

    // Reverb return: built synchronously so the GS level knob has somewhere to land even
    // while the worklet module is still loading; the FDN connects into it when ready.
    reverbReturn := context.createGain()
    reverbReturn.gain.value = @fx.reverbLevel * REVERB_RETURN_TRIM
    reverbReturn.connect out
    @reverbReturn = reverbReturn

    // Chorus bus: native nodes, so unlike the reverb it exists immediately.
    @chorusBus?.dispose()
    chorusBus := createChorusNode context
    chorusBus.output.connect out
    chorusBus.setParams @fx.chorus
    @chorusBus = chorusBus

    // GS delay bus: native nodes too. Its reverb send joins the per-channel sends on
    // the FDN below (40 01 5A: the echoes bloom into the room like any other source).
    @delayBus?.dispose()
    delayBus := createDelayNode context
    delayBus.output.connect out
    delayBus.setParams @fx.delay
    @delayBus = delayBus

    // GM Level 1 Developer Guidelines
    @channelOut = [0...16].map (c) =>
      channel := @channels[c]

      // Volume / Expression
      vol := context.createGain()
      vol.gain.value = pow channel.volume * channel.expression, 2
      vol.connect out

      // Pan is applied per-voice: channel CC pan and each sample's pan
      // generator sum into a single equal-power stage in the voice itself
      // (like FluidSynth's default CC10 -> GEN_PAN modulator), so the channel
      // input is a plain gain
      input := context.createGain()
      input.connect vol

      let tuning: ConstantSourceNode, pitchBend: ConstantSourceNode, pitchBendSensitivity
      // Tuning
      if (context.createConstantSource)
        tuning = context.createConstantSource()
        tuning.offset.value = 0
        tuning.start(0)

        // Pitch Bend
        pitchBend = context.createConstantSource()
        pitchBend.offset.value = channel.pitchBend
        pitchBend.start(0)

        pitchBendSensitivity = context.createGain()
        pitchBendSensitivity.gain.value = channel.pitchBendSensitivity
        pitchBend.connect pitchBendSensitivity
        // Pitch bend adds to tuning cents
        pitchBendSensitivity.connect tuning.offset
      else
        // ConstantSourceNode doesn't exist in Safari < 14.1
        tuning = DummyConstantSourceNode() as! ConstantSourceNode
        pitchBend = DummyConstantSourceNode() as! ConstantSourceNode
        pitchBendSensitivity = context.createGain()

      // TODO: >= v0.6 Master tuning

      // Effects channel
      //   per voice   / per channel /  shared
      // voice -> send -> reverbLevel -> reverb -> out
      //
      // The send itself is per voice (`reverbEffectsSend` is a zone generator, see
      // `addVoice`); this node is the channel's scaling of all of them, and the node CC 91
      // will move once it has a sensible default to move from.
      //
      // Gain 1, not `channel.fx1`: `control[91]` is zero until a file sends CC 91, and using
      // it directly would silence every bank's own sends on every file that doesn't. The
      // per-voice send handles CC 91 instead (see `addVoice`), so a file that never touches
      // CC 91 and plays a bank with no zone sends stays exactly as dry as it always was.
      reverbLevel := context.createGain()
      reverbLevel.gain.value = 1
      reverbSends.push reverbLevel

      // Chorus mirror of the above — connected immediately, the bus is synchronous.
      chorusLevel := context.createGain()
      chorusLevel.gain.value = 1
      chorusLevel.connect chorusBus.input

      // Delay mirror, same pattern.
      delayLevel := context.createGain()
      delayLevel.gain.value = 1
      delayLevel.connect delayBus.input

      dispose: ->
        pitchBend.stop()
        tuning.stop()

      gain: vol.gain
      // audio node which outputs the sum total of channel tuning and pitch bend in cents
      tuning: tuning
      pitchBend: pitchBend.offset
      pitchBendSensitivity: pitchBendSensitivity.gain
      reverb: reverbLevel
      chorus: chorusLevel
      delay: delayLevel
      input: input
      voices: [] // active voices
      sustained: [] // voices whose key is up but the pedal (CC 64) holds them

    // One FDN for all 16 channels: SF2's model is a single room, and per voice reverb
    // would be ~32 independent filter banks per sample for the same sound.
    //
    // Built after `addModule` resolves, which is why callers have to await
    // `SF2Synth::prepareContext` before rendering. The channel send nodes already exist,
    // so voices created in between simply feed a bus that is not connected yet.
    ReverbWorklet.prepareContext context
    .then =>
      // A later `bind` won the context; this graph is already orphaned.
      return unless @context is context

      bus := ReverbWorklet.createReverbNode context
      bus.connect reverbReturn
      reverbSends.forEach (send) -> send.connect bus
      delayBus.reverbSend.connect bus
      // The bus is built late (after `addModule`), so a reverb-time SysEx that arrived
      // while the module was loading has to be carried onto the fresh node here.
      if @fx.rt60 !== REVERB_RT60
        bus.parameters.get("rt60")?.setValueAtTime @fx.rt60, 0
      @reverbBus = bus
    .catch (e) ->
      // Dry rather than silent: everything downstream of the sends is optional.
      console.warn "SF2 reverb bus unavailable, rendering dry", e

    return out

  controlChange(channel: MidiChannel, n: number, v: number, t: number)
    c := channel.id

    // Audio context barfs if t is < 0 but it is handly to pass in negative t
    // values to sync play time in song with audio context
    t = max 0, t

    switch n
      when 0x07 // Volume MSB
        @setVolume(@channelOut[c], channel, v/127 * channel.expression, t)
      when 0x0B // expresson MSB
        @setVolume(@channelOut[c], channel, v/127 * channel.volume, t)
      when 0x5B, 0x5D, 0x5E // Reverb / chorus / delay send — retune sounding (and pedal-held) voices.
        level := v / 127
        update := (voice: any) ->
          voice.fxSends?.forEach (s: {cc: number, param: AudioParam, base: number}) ->
            if s.cc is n
              s.param.setValueAtTime max(s.base, level), max(0, t)
        {voices, sustained} := @channelOut[c]
        voices.forEach ({voice}) -> update voice
        sustained.forEach update

    // Pan (0x0A, 0x2A) and anything else voices handle themselves
    @channelOut[c].voices.forEach ({voice}) ->
      voice.controlChange?(n, v, t)

  // GS effect-block SysEx, normalized by ChannelData.sysEx. Persisted in @fx so a later
  // bind (seek, re-render) rebuilds the same room.
  fxParam(name: string, value: number)
    switch name
      when "reset"
        @fxParam "reverbLevel", 1
        @fxParam "reverbTime", REVERB_RT60
        @fx.chorus = { rateHz: 0.6, depthMs: 6, delayMs: 16, feedback: 0.06, level: 1 }
        @chorusBus?.setParams @fx.chorus
        @fx.delay = {...DELAY_DEFAULTS}
        @delayBus?.setParams @fx.delay
      // GS delay block (40 01 50-5A), raw wire values — the bus owns the scaling tables.
      when "delayMacro"
        macro := DELAY_MACROS[value]
        if macro
          Object.assign @fx.delay, macro
          @delayBus?.setParams macro
      when "delayPreLpf", "delayTimeC", "delayRatioL", "delayRatioR", "delayLevelC",
           "delayLevelL", "delayLevelR", "delayLevel", "delayFeedback", "delayReverbSend"
        key := name[5].toLowerCase() + name[6..] // "delayTimeC" -> "timeC"
        @fx.delay[key] = value
        @delayBus?.setParams { [key]: value }
      when "reverbLevel"
        @fx.reverbLevel = value
        @reverbReturn?.gain.setValueAtTime value * REVERB_RETURN_TRIM, @context?.currentTime ?? 0
      when "reverbTime"
        @fx.rt60 = value
        // An AudioParam, not a port message: automation is scheduled on the render clock,
        // so the retune lands on the same quantum in every render of the same song. A
        // message would arrive whenever the main thread next ran, which is not.
        @reverbBus?.parameters.get("rt60")?.setValueAtTime value, @context?.currentTime ?? 0
      when "chorusLevel"
        @fx.chorus.level = value
        @chorusBus?.setParams { level: value }
      when "chorusRate"
        @fx.chorus.rateHz = value
        @chorusBus?.setParams { rateHz: value }
      when "chorusDepth"
        @fx.chorus.depthMs = value
        @chorusBus?.setParams { depthMs: value }
      when "chorusFeedback"
        @fx.chorus.feedback = value
        @chorusBus?.setParams { feedback: value }
      when "chorusDelay"
        @fx.chorus.delayMs = value
        @chorusBus?.setParams { delayMs: value }

  channelTuning(channel: MidiChannel, cents: number, t: number)
    t = max 0, t
    c := channel.id

    epsilon := channel.shared.deltaTimeToSeconds(1)
    // ChannelData updates tuningFine/tuningCoarse before this handler runs,
    // so channel.tuning is already the new total: this is a step, not a ramp.
    // Fine for RPN tuning -- hardware applies it immediately.
    holdAndRamp @channelOut[c].tuning.offset, channel.tuning, cents, t, epsilon

  pitchBend(channel: MidiChannel, bend: number, t: number)
    t = max 0, t
    c := channel.id

    // duration of one tick in seconds
    epsilon := channel.shared.deltaTimeToSeconds(1)
    // channel.pitchBend still holds the previous value here (ChannelData
    // updates it after the handler returns)
    holdAndRamp @channelOut[c].pitchBend, channel.pitchBend, bend, t, epsilon

  pitchBendSensitivity(channel: MidiChannel, cents: number, t: number)
    t = max 0, t
    c := channel.id

    epsilon := channel.shared.deltaTimeToSeconds(1)
    holdAndRamp @channelOut[c].pitchBendSensitivity, channel.pitchBendSensitivity, cents, t, epsilon

  debugStatus()
    // console.log channels, channelOut
    fmt := (v: number) -> v.toFixed(2)
    @channelOut.map ({gain, tuning, voices}, id) ->
      ```
        ${id}
        #: ${voices.length} v: ${fmt(gain.value)} t: ${fmt(tuning.offset.value)}
      ```
    .join("\n")

export default Channel

type ChannelOut =
  dispose: () => void
  gain: AudioParam
  tuning: ConstantSourceNode
  pitchBend: AudioParam
  pitchBendSensitivity: AudioParam
  // Present only when the reverb bus is enabled.
  reverb: GainNode?
  chorus: GainNode?
  delay: GainNode?
  input: GainNode
  voices: { voice: any, n: number }[]
  sustained: any[]
